import { BaseService } from './BaseService';
import type { PagedResult } from '../types/tenant';
import type {
  ExternalSystemDto,
  ExternalSystemDetailDto,
  ExternalApiMethodDto,
  ExternalSystemFilters,
  CreateExternalSystemRequest,
  UpdateExternalSystemRequest,
  CreateApiMethodRequest,
  UpdateApiMethodRequest,
  TestApiMethodRequest,
  ApiExecutionResult,
} from '../types/externalSystem';

/**
 * External system service for managing external systems and API methods.
 */
export class ExternalSystemService extends BaseService {
  /**
   * Gets a paginated list of external systems with optional filtering.
   */
  async getExternalSystems(filters?: ExternalSystemFilters): Promise<PagedResult<ExternalSystemDto>> {
    const queryParams = new URLSearchParams();
    if (filters?.pageNumber) queryParams.append('pageNumber', filters.pageNumber.toString());
    if (filters?.pageSize) queryParams.append('pageSize', filters.pageSize.toString());
    if (filters?.searchTerm) queryParams.append('searchTerm', filters.searchTerm);
    if (filters?.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());

    const url = `/external-systems${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<PagedResult<ExternalSystemDto>>(url);
  }

  /**
   * Gets an external system by ID.
   */
  async getExternalSystem(id: string): Promise<ExternalSystemDetailDto> {
    return this.get<ExternalSystemDetailDto>(`/external-systems/${id}`);
  }

  /**
   * Creates a new external system.
   */
  async createExternalSystem(data: CreateExternalSystemRequest): Promise<ExternalSystemDetailDto> {
    return this.post<ExternalSystemDetailDto>('/external-systems', data);
  }

  /**
   * Updates an existing external system.
   */
  async updateExternalSystem(id: string, data: UpdateExternalSystemRequest): Promise<ExternalSystemDetailDto> {
    return this.put<ExternalSystemDetailDto>(`/external-systems/${id}`, data);
  }

  /**
   * Deletes an external system.
   */
  async deleteExternalSystem(id: string): Promise<void> {
    return this.delete<void>(`/external-systems/${id}`);
  }

  /**
   * Gets API methods for an external system.
   */
  async getApiMethods(systemId: string, isActive?: boolean): Promise<ExternalApiMethodDto[]> {
    const queryParams = new URLSearchParams();
    if (isActive !== undefined) queryParams.append('isActive', isActive.toString());

    const url = `/external-systems/${systemId}/methods${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<ExternalApiMethodDto[]>(url);
  }

  /**
   * Creates a new API method.
   */
  async createApiMethod(systemId: string, data: CreateApiMethodRequest): Promise<ExternalApiMethodDto> {
    return this.post<ExternalApiMethodDto>(`/external-systems/${systemId}/methods`, data);
  }

  /**
   * Updates an existing API method.
   */
  async updateApiMethod(
    systemId: string,
    methodId: string,
    data: UpdateApiMethodRequest
  ): Promise<ExternalApiMethodDto> {
    return this.put<ExternalApiMethodDto>(`/external-systems/${systemId}/methods/${methodId}`, data);
  }

  /**
   * Tests an API method execution.
   */
  async testApiMethod(
    systemId: string,
    methodId: string,
    request: TestApiMethodRequest
  ): Promise<ApiExecutionResult> {
    return this.post<ApiExecutionResult>(`/external-systems/${systemId}/methods/${methodId}/test`, request);
  }
}

export const externalSystemService = new ExternalSystemService();

// Re-export DTOs/requests for convenience in pages/components.
export type {
  ExternalSystemDto,
  ExternalSystemDetailDto,
  ExternalApiMethodDto,
  ExternalSystemFilters,
  CreateExternalSystemRequest,
  UpdateExternalSystemRequest,
  CreateApiMethodRequest,
  UpdateApiMethodRequest,
  TestApiMethodRequest,
  ApiExecutionResult,
} from '../types/externalSystem';
